/**
 * UnifiedInconsistencias - Tela de Inconsistências do Empregado
 * Mostra horário esperado da escala e lista de inconsistências por dia
 * Permite justificar cada inconsistência
 */

import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { 
  Clock, AlertTriangle, CheckCircle, XCircle, ChevronRight,
  Calendar, FileText, Send, Loader2, AlertCircle, Info,
  ArrowRight, Coffee, LogIn, LogOut, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeamento de tipos de inconsistência para exibição
const TIPOS_INCONSISTENCIA = {
  entrada_antecipada: { label: 'Entrada antes do horário', cor: 'bg-amber-100 text-amber-800', icone: LogIn },
  entrada_atrasada: { label: 'Entrada atrasada', cor: 'bg-red-100 text-red-800', icone: LogIn },
  saida_antecipada: { label: 'Saída antecipada', cor: 'bg-orange-100 text-orange-800', icone: LogOut },
  saida_posterior: { label: 'Hora Extra', cor: 'bg-blue-100 text-blue-800', icone: LogOut },
  intervalo_curto: { label: 'Intervalo menor que 1h', cor: 'bg-purple-100 text-purple-800', icone: Coffee },
  retorno_antecipado: { label: 'Retorno antecipado do intervalo', cor: 'bg-purple-100 text-purple-800', icone: Coffee },
  falta: { label: 'Falta', cor: 'bg-red-100 text-red-800', icone: XCircle },
  marcacao_extra: { label: 'Marcação extra', cor: 'bg-slate-100 text-slate-800', icone: AlertCircle },
  // Novos tipos
  mudanca_escala: { label: 'Possível Mudança de Escala', cor: 'bg-indigo-100 text-indigo-800', icone: RefreshCw },
  violacao_interjornada: { label: 'VIOLAÇÃO DE INTERJORNADA', cor: 'bg-red-200 text-red-900 font-bold', icone: AlertTriangle, grave: true }
};

const STATUS_CORES = {
  pendente: 'bg-amber-100 text-amber-800 border-amber-200',
  justificada: 'bg-blue-100 text-blue-800 border-blue-200',
  aprovada: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejeitada: 'bg-red-100 text-red-800 border-red-200'
};

const UnifiedInconsistencias = () => {
  const { jornyxToken, jornyxUser, isOnline } = useUnifiedAuth();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState({
    horario_esperado_padrao: null,
    dias: [],
    total_inconsistencias: 0,
    pendentes: 0
  });
  const [tiposJustificativa, setTiposJustificativa] = useState([]);
  const [showJustificativaDialog, setShowJustificativaDialog] = useState(false);
  const [inconsistenciaSelecionada, setInconsistenciaSelecionada] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [justificativa, setJustificativa] = useState({
    tipo: '',
    motivo: '',
    descricao: '',
    nova_escala_id: '',
    proxima_folga: ''
  });
  // Manter último tipo/motivo de justificativa selecionado
  const [ultimaJustificativa, setUltimaJustificativa] = useState({ tipo: '', motivo: '' });
  const [escalasDisponiveis, setEscalasDisponiveis] = useState([]);
  const [showConfirmarMudancaDialog, setShowConfirmarMudancaDialog] = useState(false);

  useEffect(() => {
    if (jornyxToken) {
      carregarDados();
      carregarTiposJustificativa();
      carregarEscalasDisponiveis();
    }
  }, [jornyxToken]);

  const carregarEscalasDisponiveis = async () => {
    try {
      const res = await fetch(`${API_URL}/api/app/minha-escala`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEscalasDisponiveis(data.escalas_disponiveis || []);
      }
    } catch (err) {
      console.error('Erro ao carregar escalas:', err);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/app/inconsistencias`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDados(data);
      }
    } catch (err) {
      console.error('Erro ao carregar inconsistências:', err);
      toast.error('Erro ao carregar inconsistências');
    } finally {
      setLoading(false);
    }
  };

  const carregarTiposJustificativa = async () => {
    try {
      const res = await fetch(`${API_URL}/api/app/tipos-justificativa`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTiposJustificativa(data.tipos || []);
      }
    } catch (err) {
      console.error('Erro ao carregar tipos:', err);
    }
  };

  const carregarEscalas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/escalas-trabalho?empresa_id=${jornyxUser?.empresa_id}`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEscalasDisponiveis(data.filter(e => e.ativa !== false));
      }
    } catch (err) {
      console.error('Erro ao carregar escalas:', err);
    }
  };

  const abrirJustificativa = (inconsistencia) => {
    setInconsistenciaSelecionada(inconsistencia);
    // Manter último tipo/motivo de justificativa selecionado
    setJustificativa({
      tipo: ultimaJustificativa.tipo || '',
      motivo: ultimaJustificativa.motivo || '',
      descricao: '',
      nova_escala_id: '',
      proxima_folga: ''
    });
    setShowJustificativaDialog(true);
  };

  const handleTipoChange = (tipo) => {
    setJustificativa({ ...justificativa, tipo });
    
    // Se for mudança de turno, carregar escalas
    if (tipo === 'mudanca_turno') {
      carregarEscalas();
    }
  };

  const enviarJustificativa = async () => {
    if (!justificativa.tipo) {
      toast.error('Selecione o tipo de justificativa');
      return;
    }
    if (!justificativa.motivo) {
      toast.error('Informe o motivo');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/app/inconsistencias/${inconsistenciaSelecionada.id}/justificar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(justificativa)
      });
      
      if (res.ok) {
        toast.success('Justificativa enviada para aprovação!');
        // Salvar último tipo/motivo para reutilizar
        setUltimaJustificativa({ tipo: justificativa.tipo, motivo: justificativa.motivo });
        setShowJustificativaDialog(false);
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar justificativa');
      }
    } catch (err) {
      toast.error('Erro ao enviar justificativa');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmarMudancaEscala = async () => {
    if (!inconsistenciaSelecionada) return;
    
    setSubmitting(true);
    try {
      const escalaDetectada = inconsistenciaSelecionada.escala_detectada;
      
      const res = await fetch(`${API_URL}/api/app/inconsistencias/${inconsistenciaSelecionada.id}/confirmar-mudanca-escala`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nova_escala_id: escalaDetectada?.escala_id,
          proxima_folga: justificativa.proxima_folga,
          motivo: justificativa.motivo || 'Confirmação de mudança de escala'
        })
      });
      
      if (res.ok) {
        toast.success('Mudança de escala confirmada! Aguardando aprovação do gestor.');
        setShowConfirmarMudancaDialog(false);
        setInconsistenciaSelecionada(null);
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao confirmar mudança');
      }
    } catch (err) {
      toast.error('Erro ao confirmar mudança de escala');
    } finally {
      setSubmitting(false);
    }
  };

  const abrirConfirmarMudanca = (inconsistencia) => {
    setInconsistenciaSelecionada(inconsistencia);
    setJustificativa({
      tipo: 'mudanca_escala',
      motivo: '',
      descricao: '',
      nova_escala_id: inconsistencia.escala_detectada?.escala_id || '',
      proxima_folga: ''
    });
    setShowConfirmarMudancaDialog(true);
  };

  const formatarData = (data) => {
    const [ano, mes, dia] = data.split('-');
    const dataObj = new Date(ano, mes - 1, dia);
    const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return `${dia}/${mes} - ${diasSemana[dataObj.getDay()]}`;
  };

  const getTipoConfig = (tipo) => {
    return TIPOS_INCONSISTENCIA[tipo] || { 
      label: tipo, 
      cor: 'bg-slate-100 text-slate-800',
      icone: AlertCircle 
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 pb-24">
      {/* Header com Horário Esperado */}
      <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-0">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Horário Esperado</span>
            </div>
            {dados.pendentes > 0 && (
              <Badge className="bg-white/20 text-white border-white/30">
                {dados.pendentes} pendente{dados.pendentes > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          {dados.horario_esperado_padrao ? (
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-white/70 text-xs">Entrada</div>
                <div className="font-bold text-lg">{dados.horario_esperado_padrao.entrada}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-white/70 text-xs">Intervalo</div>
                <div className="font-bold text-lg">{dados.horario_esperado_padrao.saida_intervalo}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-white/70 text-xs">Retorno</div>
                <div className="font-bold text-lg">{dados.horario_esperado_padrao.retorno_intervalo}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2">
                <div className="text-white/70 text-xs">Saída</div>
                <div className="font-bold text-lg">{dados.horario_esperado_padrao.saida}</div>
              </div>
            </div>
          ) : (
            <p className="text-white/80 text-sm">Sem escala vinculada</p>
          )}
          
          {dados.horario_esperado_padrao?.turno_nome && (
            <div className="mt-2 text-sm text-white/80 text-center">
              {dados.horario_esperado_padrao.turno_nome} • Intervalo de 1 hora
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nota explicativa */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Após justificar, o <strong>encarregado aprova</strong> e o RH recebe a informação finalizada.
        </p>
      </div>

      {/* Lista de Dias com Inconsistências */}
      {dados.dias.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">Nenhuma inconsistência</p>
            <p className="text-sm text-slate-500">Seu ponto está em dia!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {dados.dias.map((dia) => (
            <Card key={dia.data} className="overflow-hidden">
              <CardHeader className="bg-slate-50 py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">{formatarData(dia.data)}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {dia.marcacoes?.length || 0} marcaç{dia.marcacoes?.length === 1 ? 'ão' : 'ões'}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="py-3 px-4 space-y-3">
                {/* Marcações do dia */}
                {dia.marcacoes && dia.marcacoes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {dia.marcacoes.map((marc, idx) => (
                      <div 
                        key={idx} 
                        className="bg-slate-100 px-3 py-1 rounded-full text-sm font-mono"
                      >
                        {marc.hora}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Inconsistências do dia */}
                <div className="space-y-2">
                  {dia.inconsistencias.map((inc) => {
                    const tipoConfig = getTipoConfig(inc.tipo);
                    const Icone = tipoConfig.icone;
                    
                    return (
                      <div 
                        key={inc.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          inc.status === 'pendente' 
                            ? 'bg-red-50 border-red-200' 
                            : STATUS_CORES[inc.status] || 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${tipoConfig.cor}`}>
                            <Icone className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              {inc.hora_marcacao && inc.hora_marcacao !== '-' && (
                                <span className="font-mono font-bold text-red-600">
                                  {inc.hora_marcacao}
                                </span>
                              )}
                              <span className="text-sm font-medium text-slate-700">
                                {inc.descricao || tipoConfig.label}
                              </span>
                            </div>
                            {inc.horario_esperado && (
                              <span className="text-xs text-slate-500">
                                Esperado: {inc.horario_esperado}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {inc.status === 'pendente' ? (
                            <>
                              {/* Mudança de Escala - Botão especial */}
                              {inc.tipo === 'mudanca_escala' ? (
                                <Button 
                                  size="sm" 
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                  onClick={() => abrirConfirmarMudanca(inc)}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Confirmar Troca
                                </Button>
                              ) : inc.tipo === 'violacao_interjornada' ? (
                                /* Violação Interjornada - Apenas gestor pode justificar */
                                <Badge className="bg-red-500 text-white">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Aguardando Gestor
                                </Badge>
                              ) : (
                                /* Outros tipos - Botão normal de justificar */
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-red-300 text-red-700 hover:bg-red-100"
                                  onClick={() => abrirJustificativa(inc)}
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Justificar
                                </Button>
                              )}
                            </>
                          ) : (
                            <Badge className={STATUS_CORES[inc.status]}>
                              {inc.status === 'justificada' && 'Aguardando aprovação'}
                              {inc.status === 'aprovada' && 'Aprovada'}
                              {inc.status === 'rejeitada' && 'Rejeitada'}
                              {inc.status === 'pendente_aprovacao' && 'Aguardando aprovação'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de Justificativa */}
      <Dialog open={showJustificativaDialog} onOpenChange={setShowJustificativaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Justificar Inconsistência</DialogTitle>
            <DialogDescription>
              {inconsistenciaSelecionada && (
                <span className="text-slate-600">
                  {formatarData(inconsistenciaSelecionada.data)} • {inconsistenciaSelecionada.descricao}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Tipo de Justificativa *</Label>
              <Select value={justificativa.tipo} onValueChange={handleTipoChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposJustificativa.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {justificativa.tipo && (
                <p className="text-xs text-slate-500 mt-1">
                  {tiposJustificativa.find(t => t.id === justificativa.tipo)?.descricao}
                </p>
              )}
            </div>

            {/* Campos especiais para mudança de turno */}
            {justificativa.tipo === 'mudanca_turno' && (
              <>
                <div>
                  <Label>Nova Escala</Label>
                  <Select 
                    value={justificativa.nova_escala_id} 
                    onValueChange={(v) => setJustificativa({...justificativa, nova_escala_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a escala" />
                    </SelectTrigger>
                    <SelectContent>
                      {escalasDisponiveis.map((esc) => (
                        <SelectItem key={esc.id} value={esc.id}>
                          {esc.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data da Próxima Folga</Label>
                  <Input 
                    type="date"
                    value={justificativa.proxima_folga}
                    onChange={(e) => setJustificativa({...justificativa, proxima_folga: e.target.value})}
                  />
                </div>
              </>
            )}

            <div>
              <Label>Motivo *</Label>
              <Textarea
                placeholder="Descreva o motivo da inconsistência..."
                value={justificativa.motivo}
                onChange={(e) => setJustificativa({...justificativa, motivo: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <Label>Observações Adicionais</Label>
              <Textarea
                placeholder="Informações complementares (opcional)"
                value={justificativa.descricao}
                onChange={(e) => setJustificativa({...justificativa, descricao: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJustificativaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={enviarJustificativa} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Justificativa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Confirmar Mudança de Escala */}
      <Dialog open={showConfirmarMudancaDialog} onOpenChange={setShowConfirmarMudancaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-indigo-600" />
              Confirmar Mudança de Escala
            </DialogTitle>
            <DialogDescription>
              O sistema detectou que você pode ter trocado de escala
            </DialogDescription>
          </DialogHeader>

          {inconsistenciaSelecionada?.escala_detectada && (
            <div className="space-y-4">
              {/* Info da escala detectada */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-indigo-600" />
                  <span className="font-medium text-indigo-900">Escala Detectada</span>
                </div>
                <p className="text-indigo-800">
                  <strong>{inconsistenciaSelecionada.escala_detectada.escala_nome}</strong>
                </p>
                <p className="text-sm text-indigo-700">
                  Entrada prevista: {inconsistenciaSelecionada.escala_detectada.entrada_esperada}
                </p>
                <p className="text-sm text-indigo-600 mt-2">
                  Sua marcação: <strong>{inconsistenciaSelecionada.hora_marcacao}</strong>
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    Ao confirmar, sua escala será alterada para <strong>{inconsistenciaSelecionada.escala_detectada.escala_nome}</strong>.
                    A mudança será enviada para aprovação do seu gestor.
                  </p>
                </div>
              </div>

              <div>
                <Label>Data da Próxima Folga (opcional)</Label>
                <Input 
                  type="date"
                  value={justificativa.proxima_folga}
                  onChange={(e) => setJustificativa({...justificativa, proxima_folga: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Informe sua próxima folga para ajustar o ciclo da nova escala
                </p>
              </div>

              <div>
                <Label>Motivo (opcional)</Label>
                <Textarea
                  placeholder="Explique o motivo da troca de escala..."
                  value={justificativa.motivo}
                  onChange={(e) => setJustificativa({...justificativa, motivo: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConfirmarMudancaDialog(false);
              setInconsistenciaSelecionada(null);
            }}>
              Cancelar
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={confirmarMudancaEscala} 
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirmando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Confirmar Mudança
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedInconsistencias;
